import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Business-role gate — deny by default.
 *
 * - Anonymous users are redirected to /login (carrying the intended location).
 * - Owner-Principals are redirected to the Owner Portal (owner-governance-reports)
 *   rather than shown a forbidden wall, per the use case (criterion 5).
 * - Any other signed-in user without one of `roles` is blocked (403), not merely
 *   hidden — direct navigation is refused.
 *
 *   <Route element={<RoleRoute roles={['portfolio_director', 'asset_manager']} />}>
 *     <Route path="portfolio-director-dashboard" element={<Dashboard />} />
 *   </Route>
 */
export default function RoleRoute({ roles = [], ownerPrincipalRedirect = '/owner-governance-reports' }) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5" style={{ paddingTop: 100 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const businessRole = user?.business_role;

  // Owner-Principals never see director workspaces — send them to their portal.
  // (Skip when this route explicitly permits Owner-Principals, e.g. the Owner
  // Portal itself, to avoid a redirect loop.)
  if (businessRole === 'owner_principal' && !roles.includes('owner_principal')) {
    return <Navigate to={ownerPrincipalRedirect} replace />;
  }

  if (roles.length > 0 && !roles.includes(businessRole)) {
    return <Navigate to="/403" replace />;
  }

  return <Outlet />;
}
